import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Form, Col } from "react-bootstrap";
import "./TaskReport.css";
import AxiosInstance from "../../Services/AxiosInstance";
import { API_ENDPOINTS } from "../../Services/TaskService";
import { useNavigate } from "react-router-dom";

const TaskReport = () => {
  const [tasks, setTasks] = useState([]);
  const [expiredTasks, setExpiredTasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0); // Pagination current page
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page for pagination
  const navigate = useNavigate();
  const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#d45087", "#546de5"];
  const [project, setProject] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setprojectFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [priority, setPriority] = useState([]); 
  const [dueDateFilter, setDueDateFilter] = useState("");

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const [taskresponse, priorityresponse, projectresponse] = await Promise.all([
          AxiosInstance.get(API_ENDPOINTS.GET_TASKS_REPORT),
          AxiosInstance.get(API_ENDPOINTS.GET_PRIORIIY),
          AxiosInstance.get(
            API_ENDPOINTS.GET_PROJECTS_NAME
          )
        ]);
        setTasks(taskresponse.data || []);
        setFilteredTasks(taskresponse.data || []);
        setPriority(priorityresponse.data || []);
        setProject(projectresponse.data || []);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    // const fetchDatas = async () => {
    //   try {
    //     const projectresponse = await AxiosInstance.get(
    //       API_ENDPOINTS.GET_PROJECTS_NAME
    //     );
    //     setProject(projectresponse.data || []);
    //   } catch (error) {
    //     console.error("Error fetching users:", error);
    //   }
    // };

    // fetchDatas();
    fetchTasks();
  }, []);

  useEffect(() => {
    let updatedTasks = [...tasks];

    if (searchQuery) {
      updatedTasks = updatedTasks.filter((task) =>
        task.taskName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (priorityFilter) {
      updatedTasks = updatedTasks.filter((task) => task.priority === priorityFilter);
    }
    if (projectFilter) {
      updatedTasks = updatedTasks.filter((task) => task.project === projectFilter);
    }

    if (dueDateFilter) {
      updatedTasks = updatedTasks.filter(
        (task) => new Date(task.dueDate).toISOString().split("T")[0] === dueDateFilter
      );
    }

    setFilteredTasks(updatedTasks);

    // Update expired tasks
    setExpiredTasks(
      updatedTasks.filter((task) => new Date(task.dueDate) < new Date())
    );
  }, [searchQuery, priorityFilter, projectFilter, dueDateFilter, tasks]);

  // Data Transformation
  const statusData = tasks.reduce((acc, task) => {
    const status = task.status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusChartData = Object.keys(statusData).map((key) => ({
    name: key,
    value: statusData[key],
  }));
  
  const priorityData = tasks.reduce((acc, task) => {
    const priority = task.priority || "Unknown";
    acc[priority] = (acc[priority] || 0) + 1;
    return acc;
  }, {});

  const priorityChartData = Object.keys(priorityData).map((key) => ({
    name: key,
    value: priorityData[key],
  }));

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };
  const handleclick=(taskId)=>{
    navigate('/taskdetails',{ state: { taskId } })
  }

  // Styling
  const paperStyle = { padding: "16px", textAlign: "center" };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Total {tasks.length} Tasks
      </Typography>

      <Grid container spacing={3}>
        {/* Status Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} style={paperStyle}>
            <Typography variant="h6">Status Report</Typography>
            <PieChart width={400} height={300}>
              <Pie
                data={statusChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </Paper>
        </Grid>

        {/* Priority Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} style={paperStyle}>
            <Typography variant="h6">Priority Report</Typography>
            <BarChart width={400} height={300} data={priorityChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#82ca9d">
                {priorityChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </Paper>
        </Grid>

        {/* Expired Tasks Table */}
        <Grid item xs={12}>
          <Paper elevation={3} style={paperStyle}>
            <Typography variant="h6">OverDue Tasks</Typography>
            <div className="d-flex mb-3">
              <Col md={3}>
                <Form.Control
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Task Name"
                />
              </Col>
              <Col md={3} className="ms-4">
                <Form.Select
                  value={projectFilter}
                  onChange={(e) => setprojectFilter(e.target.value)}
                >
                  <option value="">All Projects</option>
                  {project.map((projectname) => (
                    <option key={projectname.projectId}>
                      {projectname.projectName}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} className="ms-4">
                <Form.Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="">All Priorities</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                  <option>Critical</option>
                </Form.Select>
              </Col>
              <Col md={2} className="ms-4">
                <Form.Control
                  type="date"
                  value={dueDateFilter}
                  onChange={(e) => setDueDateFilter(e.target.value)}
                />
              </Col>
            </div>
            <TableContainer >
              <Table >
                <TableHead>
                  <TableRow>
                    <TableCell>Task Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
  {expiredTasks.length > 0 ? (
    expiredTasks
      //.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
      .map((task) => (
        <TableRow
         key={task.projectTaskId} onClick={()=>handleclick(task.projectTaskId)}>
        
          <TableCell>{task.projectTaskId}</TableCell>
          <TableCell>{task.taskName}</TableCell>
          <TableCell>{task.description}</TableCell>
          <TableCell>{task.assignedToPerson}</TableCell>
          <TableCell>{task.status}</TableCell>
          <TableCell>
            {new Date(task.dueDate).toLocaleDateString()}
          </TableCell>
          <TableCell>{task.priority}</TableCell>
        </TableRow>
      ))
  ) : (
    <TableRow>
      <TableCell colSpan={6} align="center">
        No expired tasks.
      </TableCell>
    </TableRow>
  )}
</TableBody>

              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={expiredTasks.length}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskReport;
